import type {
  AzurePronunciationAnalysis,
  LingolixPronunciationAnalysis,
  LingolixPronunciationSyllable,
  VoiceNotePronunciationState,
} from "@/lib/voiceNotePronunciation";
import { Activity, AudioLines, CircleHelp, Timer, X } from "lucide-react";
import { useState } from "react";

interface PronunciationAnalysisProps {
  result: VoiceNotePronunciationState;
  onDismiss: () => void;
}

function score(value: number | null | undefined) {
  return value == null ? "-" : String(Math.round(value));
}

function wordTone(value: number | null) {
  if (value == null) return "border-white/10 bg-white/[0.035] text-white/65";
  if (value < 60) return "border-amber-300/35 bg-amber-300/[0.08] text-amber-100";
  if (value < 80) return "border-orange-300/30 bg-orange-300/[0.07] text-orange-100";
  return "border-white/14 bg-white/[0.055] text-white/85";
}

function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/40 transition-colors hover:bg-white/[0.07] hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-300/40 active:translate-y-px"
      aria-label="Dismiss pronunciation analysis"
    >
      <X size={14} />
    </button>
  );
}

function AzureView({ analysis }: { analysis: AzurePronunciationAnalysis }) {
  const primary = analysis.scores.pronunciation ?? analysis.scores.accuracy;
  const metrics = [
    ["Accuracy", analysis.scores.accuracy],
    ["Fluency", analysis.scores.fluency],
    ["Prosody", analysis.scores.prosody],
  ].filter((metric): metric is [string, number] => metric[1] != null);

  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-xl font-semibold tabular-nums text-white">
            {score(primary)}
          </span>
          <span className="text-[10px] text-white/42">overall</span>
        </div>
        {metrics.map(([label, value]) => (
          <div key={label} className="flex items-baseline gap-1.5">
            <span className="font-mono text-xs font-semibold tabular-nums text-white/80">
              {score(value)}
            </span>
            <span className="text-[10px] text-white/38">{label}</span>
          </div>
        ))}
      </div>
      {analysis.words.length ? (
        <div className="mt-1.5 flex gap-1 overflow-x-auto pb-0.5" aria-label="Word accuracy">
          {analysis.words.slice(0, 10).map((word, index) => (
            <span
              key={`${word.text}-${index}`}
              className={`shrink-0 rounded-md border px-1.5 py-1 text-[10px] ${wordTone(word.accuracy)}`}
              title={`${word.text}: ${score(word.accuracy)} accuracy`}
            >
              {word.text}
              {word.accuracy != null ? (
                <span className="ml-1 font-mono opacity-55">{Math.round(word.accuracy)}</span>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}

function syllableLabel(syllable: LingolixPronunciationSyllable) {
  if (syllable.isMissing) return "Missed";
  if (syllable.isExtra) return "Extra";
  if (syllable.accuracy != null && syllable.accuracy < 60) return "Try again";
  if (syllable.accuracy != null && syllable.accuracy >= 80) return "Clear";
  return "Close";
}

function LingolixView({ analysis }: { analysis: LingolixPronunciationAnalysis }) {
  const syllables = analysis.words.flatMap((word) => word.syllables).slice(0, 10);

  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-xl font-semibold tabular-nums text-white">
            {score(analysis.accuracy)}
          </span>
          <span className="text-[10px] text-white/42">accuracy</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-xs font-semibold tabular-nums text-white/80">
            {score(analysis.completeness)}
          </span>
          <span className="text-[10px] text-white/38">complete</span>
        </div>
        {analysis.speakingRate != null ? (
          <div className="flex items-center gap-1.5 text-white/72">
            <Timer size={11} className="text-white/35" aria-hidden="true" />
            <span className="font-mono text-xs font-semibold tabular-nums">
              {analysis.speakingRate.toFixed(1)}
            </span>
            <span className="text-[10px] text-white/38">syllables/s</span>
          </div>
        ) : null}
      </div>

      {syllables.length ? (
        <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5" aria-label="IPA sounds">
          {syllables.map((syllable, index) => (
            <div
              key={`${syllable.text}-${index}`}
              className={`w-[7.5rem] shrink-0 rounded-md border px-2 py-1.5 ${wordTone(
                syllable.accuracy
              )}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-[11px] font-semibold">
                  {syllable.text || "Sound"}
                </span>
                <span className="font-mono text-[9px] opacity-60">{score(syllable.accuracy)}</span>
              </div>
              <div className="mt-0.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-1 text-[9px] leading-3.5">
                <span className="text-white/32">Aim</span>
                <span className="truncate font-mono text-white/78">
                  /{syllable.expectedIpa || "?"}/
                </span>
                <span className="text-white/32">Heard</span>
                <span className="truncate font-mono text-white/56">
                  /{syllable.detectedIpa || "?"}/
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2 text-[8px] text-white/32">
                <span>{syllableLabel(syllable)}</span>
                {syllable.pitch !== "unknown" ? <span>{syllable.pitch} pitch</span> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function PronunciationAnalysis({ result, onDismiss }: PronunciationAnalysisProps) {
  const [showIpaHelp, setShowIpaHelp] = useState(false);

  if (result.status === "pending") {
    return (
      <section
        className="w-full min-w-0 rounded-md border border-orange-300/20 bg-[#121318] px-2.5 py-2"
        aria-live="polite"
        aria-label="Voice note pronunciation analysis in progress"
      >
        <div className="flex items-center gap-2.5">
          <AudioLines
            size={15}
            className="shrink-0 animate-pulse text-orange-200 motion-reduce:animate-none"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-2">
            <p className="text-xs font-semibold text-white">Checking voice note pronunciation</p>
            <p className="truncate text-[10px] text-white/38">
              Your tutor can answer while this runs.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (result.status === "unavailable") {
    return (
      <section
        className="flex w-full min-w-0 items-center gap-2.5 rounded-md border border-amber-300/20 bg-amber-300/[0.045] px-2.5 py-2"
        role="status"
      >
        <Activity size={14} className="shrink-0 text-amber-200/75" aria-hidden="true" />
        <p className="min-w-0 flex-1 truncate text-xs text-white/72">
          Voice note pronunciation unavailable. Your voice note still reached the tutor.
        </p>
        <DismissButton onDismiss={onDismiss} />
      </section>
    );
  }

  const isIpa = result.analysis.kind === "lingolix";
  return (
    <section
      className="w-full min-w-0 overflow-hidden rounded-md border border-white/10 bg-[#121318] px-2.5 py-2"
      aria-live="polite"
      aria-label="Voice note pronunciation analysis"
    >
      <div className="flex items-center gap-2">
        <h3 className="min-w-0 flex-1 text-xs font-semibold text-white">
          {isIpa ? "Voice note pronunciation (IPA)" : "Voice note pronunciation"}
        </h3>
        {isIpa ? (
          <button
            type="button"
            onClick={() => setShowIpaHelp((current) => !current)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/42 transition-colors hover:bg-white/[0.07] hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-300/40 active:translate-y-px"
            aria-label="What is IPA?"
            aria-expanded={showIpaHelp}
          >
            <CircleHelp size={14} />
          </button>
        ) : null}
        <DismissButton onDismiss={onDismiss} />
      </div>

      {showIpaHelp ? (
        <p className="mt-1.5 rounded-md border border-white/10 bg-white/[0.025] px-2 py-1.5 text-[10px] leading-4 text-white/55">
          IPA means International Phonetic Alphabet. It writes speech sounds as symbols, so spelling
          does not get in the way. Aim is the expected sound; Heard is the closest sound detected.
        </p>
      ) : null}

      <div className="mt-1.5">
        {result.analysis.kind === "azure" ? (
          <AzureView analysis={result.analysis} />
        ) : (
          <LingolixView analysis={result.analysis} />
        )}
      </div>
    </section>
  );
}
